import { useState } from "react";
import { motion } from "framer-motion";
import { X, HelpCircle } from "lucide-react";
import { api } from "../contexts/AuthContext";

const CATEGORIES = [
  "Something isn't working",
  "Room problem",
  "Account problem",
  "Bug",
  "Safety concern",
  "Other",
];

export default function ReportIssueModal({ onClose }) {
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!category || description.trim().length < 5) {
      setError("Please select a category and describe the issue.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/reports/issue", { category, description: description.trim() });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-[#0E0E17] border border-purple-500/25 rounded-2xl w-full max-w-sm shadow-2xl"
        onClick={e => e.stopPropagation()}>

        <div className="flex items-center justify-between px-5 py-4 border-b border-purple-500/15">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-4 h-4 text-purple-400" />
            <h2 className="font-bold text-white font-[Outfit]">Report an Issue</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5">
          {done ? (
            <div className="text-center py-6 space-y-3">
              <div className="w-12 h-12 mx-auto rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-purple-400" />
              </div>
              <p className="font-semibold text-white">Thanks for letting us know</p>
              <p className="text-sm text-slate-400">We'll look into this and work on it.</p>
              <button onClick={onClose}
                className="px-6 py-2 bg-gradient-to-r from-violet-600 to-cyan-500 text-white text-sm font-semibold rounded-xl hover:opacity-90">
                Done
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-400 mb-4">Tell us what's going on. We'll keep your identity confidential.</p>

              {/* Category */}
              <div className="space-y-1.5 mb-4">
                {CATEGORIES.map(c => (
                  <label key={c}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-all ${
                      category === c ? "border-purple-500/50 bg-purple-500/10" : "border-slate-700 hover:border-slate-600"
                    }`}>
                    <input type="radio" name="issue_category" value={c}
                      checked={category === c} onChange={() => setCategory(c)}
                      className="accent-purple-500 shrink-0" />
                    <span className="text-sm text-slate-200">{c}</span>
                  </label>
                ))}
              </div>

              {/* Description */}
              <textarea
                data-testid="issue-description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe what happened..."
                rows={3}
                maxLength={1000}
                className="w-full bg-[#141322] border border-slate-700 rounded-xl py-2.5 px-3 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-purple-500 resize-none transition-colors mb-3"
              />

              {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

              <div className="flex gap-2">
                <button onClick={onClose}
                  className="flex-1 py-2.5 border border-slate-700 text-slate-300 rounded-xl text-sm hover:border-slate-500">
                  Cancel
                </button>
                <button
                  data-testid="submit-issue-btn"
                  onClick={handleSubmit}
                  disabled={!category || loading}
                  className="flex-1 py-2.5 bg-gradient-to-r from-violet-600 to-cyan-500 text-white font-semibold rounded-xl text-sm hover:opacity-90 disabled:opacity-40">
                  {loading ? "Sending..." : "Send Report"}
                </button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
